import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService from '../services/adminService';
import type { User } from '../types/auth';

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

function parseFarmIds(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const id = part.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export default function AdminForecastPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [sampleProcessing, setSampleProcessing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearingCadi, setClearingCadi] = useState(false);
  const [result, setResult] = useState<{ status: string; message: string; result?: any } | null>(null);
  const [error, setError] = useState('');
  const [sampleFarmIds, setSampleFarmIds] = useState('');
  const [sampleLimit, setSampleLimit] = useState(3);
  const [sendWeatherMobile, setSendWeatherMobile] = useState(true);
  const [forceIndexForecast, setForceIndexForecast] = useState(true);

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  if (me && !isAdminUser(me)) {
    navigate('/', { replace: true });
    return null;
  }

  const handleClearCadi = async () => {
    const ok = window.confirm(
      'Delete all saved CADI drought clips for every farm?\n\nUse this after you publish a new drought map. The next time a field is opened, only the current month is clipped from the new map. When the next month starts, that month is clipped from its own band. This cannot be undone.'
    );
    if (!ok) return;

    setClearingCadi(true);
    setResult(null);
    setError('');
    try {
      const data = await adminService.clearCadiTimeseries();
      setResult(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to clear CADI drought data';
      setError(String(msg));
    } finally {
      setClearingCadi(false);
    }
  };

  const handleClear = async () => {
    const ok = window.confirm(
      'Delete all stored NDVI/NDMI forecast alerts and reset imagery state so the next run is not skipped?'
    );
    if (!ok) return;

    setClearing(true);
    setResult(null);
    setError('');
    try {
      const data = await adminService.clearForecastIndexAlerts();
      setResult(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to clear NDVI/NDMI forecast alerts';
      setError(String(msg));
    } finally {
      setClearing(false);
    }
  };

  const handleTrigger = async () => {
    setProcessing(true);
    setResult(null);
    setError('');
    try {
      const data = await adminService.triggerForecastProcessing();
      setResult(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to trigger forecast processing';
      setError(String(msg));
    } finally {
      setProcessing(false);
    }
  };

  const handleSampleTrigger = async () => {
    const farmIds = parseFarmIds(sampleFarmIds);
    if (farmIds.length === 0 && (!sampleLimit || sampleLimit < 1)) {
      setError('Enter farm IDs, or set a sample limit of at least 1.');
      return;
    }

    setSampleProcessing(true);
    setResult(null);
    setError('');
    try {
      const data = await adminService.triggerForecastProcessing({
        farm_ids: farmIds.length > 0 ? farmIds : undefined,
        limit: farmIds.length > 0 ? undefined : sampleLimit,
        send_weather_mobile: sendWeatherMobile,
        force_index_forecast: forceIndexForecast,
      });
      setResult(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to run sample forecast processing';
      setError(String(msg));
    } finally {
      setSampleProcessing(false);
    }
  };

  const busy = processing || sampleProcessing || clearing || clearingCadi;

  return (
    <AdminLayout title="Forecast Processing">
      <p className="text-sm text-gray-600 mb-4">
        Manually trigger the field forecast pipeline (same as the 5 PM Celery job). This will:
      </p>
      <ol className="text-sm text-gray-600 mb-6 list-decimal list-inside space-y-1">
        <li>Fetch farm geometries from the external explorer API</li>
        <li>Refresh next-day weather rain/heat outlook from OpenWeather (every run)</li>
        <li>Run NDVI/NDMI statistical forecasts when new Sentinel imagery is available</li>
        <li>Store one layperson alert each for vegetation, moisture, rain, and heat</li>
        <li>Auto-send tomorrow's weather alerts only to digisaka.app mobile</li>
      </ol>
      <p className="text-xs text-gray-500 mb-6">
        Schedule: weather forecasts + weather mobile push at 17:00 PHT. Other alerts
        (vegetation / moisture / NDVI / NDMI) are auto-sent at 06:00 PHT.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-900 border border-red-200 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
          <div className="font-semibold text-emerald-900 mb-1">
            {result.status === 'success' ? 'Completed successfully' : result.status}
          </div>
          <div className="text-emerald-800 mb-1">{result.message}</div>
          {result.result && (
            <pre className="mt-2 p-2 bg-emerald-100 rounded text-xs overflow-x-auto max-h-96">
              {JSON.stringify(result.result, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Sample / limited run */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900 mb-1">Test a few farms</h2>
        <p className="text-xs text-gray-500 mb-4">
          Run weather + vegetation/moisture forecast for specific farm IDs, or the first N
          explorer farms. Useful for quick end-to-end testing without processing everything.
        </p>

        <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="sample-farm-ids">
          Farm IDs (optional — comma or space separated)
        </label>
        <textarea
          id="sample-farm-ids"
          value={sampleFarmIds}
          onChange={(e) => setSampleFarmIds(e.target.value)}
          rows={2}
          placeholder="e.g. 12345, 67890"
          className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          disabled={busy}
        />

        <div className="mb-3 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="sample-limit">
              Sample limit (used when farm IDs are empty)
            </label>
            <input
              id="sample-limit"
              type="number"
              min={1}
              max={50}
              value={sampleLimit}
              onChange={(e) => setSampleLimit(Number(e.target.value) || 1)}
              className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              disabled={busy || parseFarmIds(sampleFarmIds).length > 0}
            />
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={sendWeatherMobile}
              onChange={(e) => setSendWeatherMobile(e.target.checked)}
              disabled={busy}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Send tomorrow&apos;s weather alerts to mobile after processing
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={forceIndexForecast}
              onChange={(e) => setForceIndexForecast(e.target.checked)}
              disabled={busy}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Force NDVI/NDMI forecast (bypass imagery-age gate)
          </label>
        </div>

        <button
          type="button"
          onClick={handleSampleTrigger}
          disabled={busy}
          className="px-5 py-2.5 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {sampleProcessing ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Running sample...
            </>
          ) : (
            <>
              <i className="ri-flask-line" />
              Run sample forecast
            </>
          )}
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
        <h2 className="text-sm font-semibold text-amber-950">New CADI drought map</h2>
        <p className="mt-1 text-xs leading-relaxed text-amber-900">
          Saved field clips stay in the database until you clear them. After a new drought map is
          published, clear the cache so the next field open reads the new map. Only the current
          month is clipped. The following month is clipped from its own band when that month starts.
        </p>
        <button
          type="button"
          onClick={handleClearCadi}
          disabled={busy}
          className="mt-3 px-5 py-2.5 bg-white text-red-700 border border-red-300 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {clearingCadi ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
              Clearing drought data...
            </>
          ) : (
            <>
              <i className="ri-delete-bin-line" />
              Clear CADI drought data
            </>
          )}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleClear}
          disabled={busy}
          className="px-5 py-2.5 bg-white text-red-700 border border-red-300 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {clearing ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
              Clearing...
            </>
          ) : (
            <>
              <i className="ri-delete-bin-line" />
              Clear NDVI/NDMI Forecast Alerts
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleTrigger}
          disabled={busy}
          className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {processing ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Processing...
            </>
          ) : (
            <>
              <i className="ri-cloudy-line" />
              Trigger all farms
            </>
          )}
        </button>
      </div>
    </AdminLayout>
  );
}
